// src/tabs/logements/CopyToChambresButton.jsx
//
// Bouton « 📋 Copier vers les autres chambres » + modale de confirmation avec
// diff champ par champ (valeur actuelle → valeur copiée). ADR-197 LOT 3 : affiché
// sur la fiche d'une CHAMBRE sous-loc (source) qui a au moins une SŒUR. Invisible
// ailleurs (logement plat, chambre unique, parent l8 non éditable).
//
// Ce composant ne PERSISTE rien : au clic « Confirmer », il remonte le sectionId
// via onCopyToChambres(sectionId) câblé dans LogementsPanel, qui patche le DRAFT
// cfg des chambres sœurs. La persistance passe par le bouton 💾 Enregistrer
// EXISTANT de la fiche. Annuler = rien.

import { useState } from "react"
import { Copy } from "lucide-react"
import { DS } from "../../lib/ui"
import { buildCopyPlan, planHasChanges, displayValue } from "../../lib/copy-to-chambres"

export default function CopyToChambresButton({ sourceLog, sisters, sectionId, onCopyToChambres }) {
  const [open, setOpen] = useState(false)

  // Garde de visibilité : chambre source avec au moins une sœur.
  const targets = Array.isArray(sisters) ? sisters : []
  if (!sourceLog || targets.length === 0) return null
  if (typeof onCopyToChambres !== 'function') return null

  function handleConfirm() {
    onCopyToChambres(sectionId)
    setOpen(false)
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`copy-to-chambres-btn-${sectionId}`}
          title="Copier ces informations vers les autres chambres de la Maison Zen"
          className="fbz-press"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: DS.card, color: DS.marine, border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl, padding: '7px 12px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: DS.fontBody,
          }}
        >
          <Copy size={13} strokeWidth={1.9} /> Copier vers les autres chambres
        </button>
      </div>
      {open && (
        <CopyConfirmModal
          sourceLog={sourceLog}
          sisters={targets}
          sectionId={sectionId}
          onConfirm={handleConfirm}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  )
}

function CopyConfirmModal({ sourceLog, sisters, sectionId, onConfirm, onCancel }) {
  const plan = buildCopyPlan(sourceLog, sisters, sectionId)
  const hasChanges = planHasChanges(plan)

  return (
    <div
      onClick={onCancel}
      data-testid="copy-to-chambres-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: DS.white, borderRadius: 14, maxWidth: 640, width: '100%',
          maxHeight: '85vh', overflowY: 'auto', padding: 24,
          boxShadow: '0 16px 48px rgba(0,0,0,.22)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: DS.fontDisplay, fontSize: 18, fontWeight: 600, color: DS.marineDark, marginBottom: 6 }}>
          <Copy size={18} strokeWidth={1.8} /> Copier vers les autres chambres — {plan.section?.label || sectionId}
        </div>
        <div style={{ fontSize: 12, color: '#555', lineHeight: 1.55, marginBottom: 14 }}>
          Les valeurs de <strong>{sourceLog.nom || 'cette chambre'}</strong> vont
          <strong> écraser</strong> celles des autres chambres ci-dessous (différences seulement).
          Les champs vides côté <strong>{sourceLog.nom || 'cette chambre'}</strong> ne sont <strong>pas</strong> copiés
          (aucune valeur n'est effacée). Rien n'est enregistré tant que tu ne cliques pas sur
          Enregistrer après confirmation.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {plan.perChambre.map(ch => (
            <div
              key={ch.chambreId}
              data-testid={`copy-chambre-${ch.chambreId}`}
              style={{
                border: `1px solid ${DS.border}`, borderRadius: 10, padding: '10px 14px',
                background: ch.alreadyUpToDate ? DS.pageBg : '#fbfcfe',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: ch.alreadyUpToDate ? 0 : 8 }}>
                <span style={{ opacity: 0.6, fontSize: 11 }}>↳ {ch.chambreId}</span> · {ch.chambreNom}
                {ch.alreadyUpToDate && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: DS.green }}>
                    ✓ déjà à jour
                  </span>
                )}
              </div>
              {!ch.alreadyUpToDate && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <tbody>
                    {ch.changes.map(c => (
                      <tr key={c.type === 'nested' ? `${c.parent}.${c.key}` : c.key}>
                        <td style={{ padding: '4px 8px 4px 0', color: '#666', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          {c.label}
                        </td>
                        <td style={{ padding: '4px 8px', color: '#999', verticalAlign: 'top' }}>
                          <span style={{ textDecoration: 'line-through' }}>{displayValue(c.from)}</span>
                        </td>
                        <td style={{ padding: '4px 0 4px 8px', color: DS.navy, fontWeight: 600, verticalAlign: 'top' }}>
                          → {displayValue(c.to)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          {plan.perChambre.length === 0 && (
            <div style={{ fontSize: 12, color: '#888' }}>Aucune autre chambre.</div>
          )}
        </div>

        {!hasChanges && (
          <div style={{
            fontSize: 12, color: DS.textSecondary, background: DS.pageBg, borderRadius: 8,
            padding: '10px 12px', marginBottom: 14,
          }}>
            ✓ Toutes les chambres sont déjà à jour pour cette section. Rien à copier.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="copy-to-chambres-cancel"
            className="fbz-press"
            style={{
              background: DS.card, color: DS.marine, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl,
              padding: '9px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: DS.fontBody,
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!hasChanges}
            data-testid="copy-to-chambres-confirm"
            className={hasChanges ? "fbz-press" : undefined}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: hasChanges ? DS.corail : '#cdd5df', color: '#fff', border: 'none',
              borderRadius: DS.radiusControl, padding: '9px 18px', fontSize: 12, fontWeight: 600,
              cursor: hasChanges ? 'pointer' : 'not-allowed', fontFamily: DS.fontBody,
            }}
          >
            <Copy size={13} strokeWidth={1.9} /> Copier vers les autres chambres
          </button>
        </div>
      </div>
    </div>
  )
}
